import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Play, Pause, Settings2, Barcode } from 'lucide-react';

interface BarcodeLocation {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface BarcodeResult {
  value: string;
  format: string;
  confidence: number;
  productName?: string;
  location?: BarcodeLocation;
}

interface Detection {
  class_id: number;
  class_name: string;
  confidence: number;
  bbox: [number, number, number, number]; // [x1, y1, x2, y2] in pixels
}

interface DetectionResponse {
  success: boolean;
  count: number;
  detections: Detection[];
  class_counts: Record<string, number>;
  detection_time: number;
  frame_base64: string;
  image_width: number;
  image_height: number;
}

// Model options for selection
const MODEL_OPTIONS = [
  { value: 'trey_v42_scan', label: 'TREYv4.2-SCAN', description: '13-class PyTorch GPU (latest)' },
  { value: 'trey_v41_scan', label: 'TREYv4.1-SCAN', description: '11-class PyTorch GPU' },
  { value: 'trey_v4_scan', label: 'TREYv4-SCAN', description: '11-class PyTorch GPU' },
  { value: 'trey_v3_scan', label: 'TREYv3-SCAN', description: '10-class multi-object model' },
  { value: 'yolov7', label: 'YOLOv7', description: 'Multi-object detection' },
  { value: 'chanel_demo', label: 'EfficientNet', description: 'Single-object classification' },
  { value: 'demo', label: 'Detectron2', description: 'Object detection' },
  { value: 'dev', label: 'Dev (Barcode)', description: 'Barcode demo' },
];

// Dynamic colors for classes
const getClassColor = (className: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#FFE66D', '#FF6B9D', '#C44569',
    '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3', '#A8D8EA',
    '#FF9F43', '#EE5A24', '#0abde3', '#10ac84', '#5f27cd'
  ];
  // Generate consistent color based on class name hash
  let hash = 0;
  for (let i = 0; i < className.length; i++) {
    hash = className.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export default function MultiObjectDetectionView() {
  const navigate = useNavigate();
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionData, setDetectionData] = useState<DetectionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedModel, setSelectedModel] = useState('trey_v42_scan');
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.5);
  const [refreshInterval, setRefreshInterval] = useState(125); // ms minimum delay between detections (~4 FPS)
  const [showSettings, setShowSettings] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [classColors, setClassColors] = useState<Record<string, string>>({});
  const isDetectingRef = useRef(false); // Track if detection is in progress
  const [barcodeResults, setBarcodeResults] = useState<BarcodeResult[]>([]);
  const [enableBarcode, setEnableBarcode] = useState(true);
  const [eanDatabase, setEanDatabase] = useState<Map<string, string>>(new Map());

  // Dual camera support
  const [dualCameraMode, setDualCameraMode] = useState(true);
  const [rightCameraData, setRightCameraData] = useState<DetectionResponse | null>(null);
  const rightCanvasRef = useRef<HTMLCanvasElement>(null);
  const rightImageRef = useRef<HTMLImageElement>(null);

  // Load EAN database on mount
  useEffect(() => {
    const loadEanDatabase = async () => {
      try {
        const response = await fetch('/KIEHLS_EANs.csv');
        const text = await response.text();
        const lines = text.split('\n');
        const db = new Map<string, string>();

        lines.forEach((line, index) => {
          if (index === 0) return; // Skip header
          const columns = line.split(',');
          if (columns.length >= 6) {
            const ean = columns[4]?.trim();
            const name = columns[5]?.trim();
            if (ean && name) {
              db.set(ean, name);
            }
          }
        });

        setEanDatabase(db);
        console.log(`Loaded ${db.size} EAN entries`);
      } catch (err) {
        console.error('Failed to load EAN database:', err);
      }
    };
    loadEanDatabase();
  }, []);

  // Barcode scanning function - returns barcodes with location data
  const scanBarcode = async (): Promise<{ barcodes: BarcodeResult[], frame?: string }> => {
    try {
      const response = await fetch('http://localhost:8001/api/detect?model=dynamsoft');
      const data = await response.json();

      if (data.success && data.barcodes && data.barcodes.length > 0) {
        const barcodes = data.barcodes.map((barcode: any) => {
          const productName = eanDatabase.get(barcode.value);
          // Parse location from Dynamsoft response
          let location: BarcodeLocation | undefined;
          if (barcode.location) {
            // Dynamsoft returns { points: [[x,y], [x,y], ...] } format
            if (barcode.location.points && Array.isArray(barcode.location.points)) {
              const points = barcode.location.points;
              const xs = points.map((p: any) => p[0] !== undefined ? p[0] : p.x);
              const ys = points.map((p: any) => p[1] !== undefined ? p[1] : p.y);
              location = {
                x1: Math.min(...xs),
                y1: Math.min(...ys),
                x2: Math.max(...xs),
                y2: Math.max(...ys)
              };
            } else if (Array.isArray(barcode.location)) {
              // Alternative: direct array format
              const points = barcode.location;
              const xs = points.map((p: any) => p[0] !== undefined ? p[0] : p.x);
              const ys = points.map((p: any) => p[1] !== undefined ? p[1] : p.y);
              location = {
                x1: Math.min(...xs),
                y1: Math.min(...ys),
                x2: Math.max(...xs),
                y2: Math.max(...ys)
              };
            } else if (barcode.location.x1 !== undefined) {
              // Already in bbox format
              location = barcode.location;
            }
          }
          return {
            value: barcode.value,
            format: barcode.format,
            confidence: barcode.confidence || 100,
            productName: productName,
            location: location
          };
        });
        return { barcodes, frame: data.frame_base64 };
      }
      return { barcodes: [], frame: data.frame_base64 };
    } catch (err) {
      console.error('Barcode scan error:', err);
      return { barcodes: [] };
    }
  };

  const runDetection = async () => {
    // Skip if already detecting (prevents queue buildup)
    if (isDetectingRef.current) return;
    isDetectingRef.current = true;
    setIsDetecting(true);
    setError(null);

    try {
      // Choose endpoint based on dual camera mode
      const endpoint = dualCameraMode
        ? `http://localhost:8001/api/detect/dual?model=${selectedModel}&confidence=${confidenceThreshold}`
        : `http://localhost:8001/api/detect?model=${selectedModel}&confidence=${confidenceThreshold}`;

      // Run object detection and barcode scanning in parallel
      const detectionPromise = fetch(endpoint);
      const barcodePromise = enableBarcode ? scanBarcode() : Promise.resolve({ barcodes: [] });

      const [response, barcodeData] = await Promise.all([detectionPromise, barcodePromise]);
      const data = await response.json();

      // Update barcode results
      setBarcodeResults(barcodeData.barcodes);

      if (data.success) {
        if (dualCameraMode) {
          // Process dual camera response
          const processDetections = (cameraData: any) => {
            if (!cameraData?.success) return { detections: [], classCounts: {}, count: 0, frame_base64: '' };

            const filteredDetections = (cameraData.detections || []).filter(
              (det: Detection) => det.confidence >= confidenceThreshold
            );

            const classCounts: Record<string, number> = {};
            filteredDetections.forEach((det: Detection) => {
              classCounts[det.class_name] = (classCounts[det.class_name] || 0) + 1;
            });

            return {
              detections: filteredDetections,
              classCounts,
              count: filteredDetections.length,
              frame_base64: cameraData.frame_base64 ? `data:image/jpeg;base64,${cameraData.frame_base64}` : '',
              detection_time: cameraData.detection_time,
              image_width: cameraData.image_width,
              image_height: cameraData.image_height
            };
          };

          const leftData = processDetections(data.left);
          const rightData = processDetections(data.right);

          // Update colors for all detected classes
          const newColors = { ...classColors };
          [...leftData.detections, ...rightData.detections].forEach((det: Detection) => {
            if (!newColors[det.class_name]) {
              newColors[det.class_name] = getClassColor(det.class_name);
            }
          });
          setClassColors(newColors);

          // Set left camera data (primary)
          setDetectionData({
            success: true,
            detections: leftData.detections,
            class_counts: leftData.classCounts,
            count: leftData.count,
            frame_base64: leftData.frame_base64,
            detection_time: leftData.detection_time || 0,
            image_width: leftData.image_width || 640,
            image_height: leftData.image_height || 480
          });

          // Set right camera data
          setRightCameraData({
            success: true,
            detections: rightData.detections,
            class_counts: rightData.classCounts,
            count: rightData.count,
            frame_base64: rightData.frame_base64,
            detection_time: rightData.detection_time || 0,
            image_width: rightData.image_width || 640,
            image_height: rightData.image_height || 480
          });
        } else {
          // Process single camera response (original logic)
          const filteredDetections = (data.detections || []).filter(
            (det: Detection) => det.confidence >= confidenceThreshold
          );

          const classCounts: Record<string, number> = {};
          filteredDetections.forEach((det: Detection) => {
            classCounts[det.class_name] = (classCounts[det.class_name] || 0) + 1;
          });

          const newColors = { ...classColors };
          filteredDetections.forEach((det: Detection) => {
            if (!newColors[det.class_name]) {
              newColors[det.class_name] = getClassColor(det.class_name);
            }
          });
          setClassColors(newColors);

          setDetectionData({
            ...data,
            frame_base64: data.frame_base64?.startsWith('data:') ? data.frame_base64 : `data:image/jpeg;base64,${data.frame_base64}`,
            detections: filteredDetections,
            class_counts: classCounts,
            count: filteredDetections.length
          });
          setRightCameraData(null);
        }
      } else {
        setError('Detection failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to connect to detection API: ' + String(err));
      console.error('Detection error:', err);
    } finally {
      setIsDetecting(false);
      isDetectingRef.current = false;
    }
  };

  // Generic function to draw bounding boxes on a canvas
  const drawBoundingBoxesOnCanvas = (
    canvas: HTMLCanvasElement | null,
    img: HTMLImageElement | null,
    detections: Detection[],
    barcodes: BarcodeResult[] = []
  ) => {
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match image
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw object detection bounding boxes
    detections.forEach((det) => {
      const [x1, y1, x2, y2] = det.bbox;
      const color = classColors[det.class_name] || getClassColor(det.class_name);

      // Draw rectangle with rounded corners effect
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

      // Semi-transparent fill
      ctx.fillStyle = color + '20';
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);

      // Draw filled background for label
      const label = `${det.class_name} ${(det.confidence * 100).toFixed(1)}%`;
      ctx.font = 'bold 14px Arial';
      const textMetrics = ctx.measureText(label);
      const textHeight = 20;

      ctx.fillStyle = color;
      ctx.fillRect(x1, y1 - textHeight - 4, textMetrics.width + 10, textHeight + 4);

      // Draw label text
      ctx.fillStyle = '#000000';
      ctx.fillText(label, x1 + 5, y1 - 8);
    });

    // Draw barcode bounding boxes (purple color)
    const barcodeColor = '#9333EA'; // Purple
    barcodes.forEach((barcode) => {
      if (barcode.location) {
        const { x1, y1, x2, y2 } = barcode.location;

        // Draw rectangle
        ctx.strokeStyle = barcodeColor;
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]); // Dashed line for barcodes
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        ctx.setLineDash([]); // Reset to solid

        // Semi-transparent fill
        ctx.fillStyle = barcodeColor + '20';
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);

        // Draw filled background for label
        const label = `${barcode.format}: ${barcode.value}`;
        ctx.font = 'bold 12px Arial';
        const textMetrics = ctx.measureText(label);
        const textHeight = 18;

        ctx.fillStyle = barcodeColor;
        ctx.fillRect(x1, y2 + 2, textMetrics.width + 10, textHeight + 4);

        // Draw label text
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(label, x1 + 5, y2 + 16);

        // If product name found, show it below
        if (barcode.productName) {
          const productLabel = barcode.productName.substring(0, 30);
          ctx.font = 'bold 11px Arial';
          const productMetrics = ctx.measureText(productLabel);

          ctx.fillStyle = '#22C55E'; // Green
          ctx.fillRect(x1, y2 + textHeight + 6, productMetrics.width + 10, 16);

          ctx.fillStyle = '#000000';
          ctx.fillText(productLabel, x1 + 5, y2 + textHeight + 18);
        }
      }
    });
  };

  const drawBoundingBoxes = () => {
    if (!detectionData) return;
    drawBoundingBoxesOnCanvas(canvasRef.current, imageRef.current, detectionData.detections, barcodeResults);
  };

  const drawRightBoundingBoxes = () => {
    if (!rightCameraData) return;
    drawBoundingBoxesOnCanvas(rightCanvasRef.current, rightImageRef.current, rightCameraData.detections, []);
  };

  // Draw bounding boxes on left camera
  useEffect(() => {
    if (detectionData && imageRef.current) {
      const img = imageRef.current;
      if (img.complete) {
        drawBoundingBoxes();
      } else {
        img.onload = () => drawBoundingBoxes();
      }
    }
  }, [detectionData, classColors, barcodeResults]);

  // Draw bounding boxes on right camera
  useEffect(() => {
    if (rightCameraData && rightImageRef.current) {
      const img = rightImageRef.current;
      if (img.complete) {
        drawRightBoundingBoxes();
      } else {
        img.onload = () => drawRightBoundingBoxes();
      }
    }
  }, [rightCameraData, classColors]);

  useEffect(() => {
    if (autoRefresh) {
      // Run immediately
      runDetection();
      intervalRef.current = setInterval(() => {
        runDetection();
      }, refreshInterval);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, selectedModel, confidenceThreshold, dualCameraMode]);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">
              Debug Detection View
            </h1>
            <p className="text-gray-400 text-sm">
              Live object detection with adjustable parameters
            </p>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <X size={20} />
            <span>Close</span>
          </button>
        </div>

        {/* Controls Bar */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Model Selection */}
            <div className="flex items-center gap-2">
              <label className="text-gray-400 text-sm">Model:</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              >
                {MODEL_OPTIONS.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Confidence Threshold */}
            <div className="flex items-center gap-2">
              <label className="text-gray-400 text-sm">Confidence:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={confidenceThreshold}
                onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
                className="w-24 accent-blue-500"
              />
              <span className="text-white font-mono text-sm w-12">
                {(confidenceThreshold * 100).toFixed(0)}%
              </span>
            </div>

            <div className="h-8 w-px bg-gray-600" />

            {/* Run Detection Button */}
            <button
              onClick={runDetection}
              disabled={isDetecting || autoRefresh}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <Play size={18} />
              {isDetecting ? 'Detecting...' : 'Run Once'}
            </button>

            {/* Auto-refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                autoRefresh
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              {autoRefresh ? <Pause size={18} /> : <Play size={18} />}
              {autoRefresh ? 'Stop Live' : 'Start Live'}
            </button>

            {/* Settings Toggle */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${
                showSettings ? 'bg-blue-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
            >
              <Settings2 size={20} />
            </button>

            {/* Detection Stats */}
            {detectionData && (
              <div className="ml-auto flex items-center gap-4 text-sm">
                <span className="text-gray-400">
                  Objects: <span className="text-white font-bold">{detectionData.count}</span>
                </span>
                <span className="text-gray-400">
                  Time: <span className="text-white font-bold">{detectionData.detection_time?.toFixed(3)}s</span>
                </span>
              </div>
            )}
          </div>

          {/* Extended Settings */}
          {showSettings && (
            <div className="mt-4 pt-4 border-t border-gray-700 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Refresh Rate:</label>
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                  className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600"
                >
                  <option value={250}>250ms (4 fps)</option>
                  <option value={500}>500ms (2 fps)</option>
                  <option value={1000}>1s (1 fps)</option>
                  <option value={2000}>2s (0.5 fps)</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Dual Camera:</label>
                <button
                  onClick={() => setDualCameraMode(!dualCameraMode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dualCameraMode ? 'bg-blue-600 text-white' : 'bg-gray-600 text-gray-300'
                  }`}
                >
                  {dualCameraMode ? 'ON' : 'OFF'}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-gray-400 text-sm">Barcode Scan:</label>
                <button
                  onClick={() => setEnableBarcode(!enableBarcode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    enableBarcode ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                  }`}
                >
                  {enableBarcode ? 'ON' : 'OFF'}
                </button>
              </div>
              <div className="text-gray-500 text-xs">
                Model: {MODEL_OPTIONS.find(m => m.value === selectedModel)?.description}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded text-red-200 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Detection View */}
          <div className="lg:col-span-3">
            {dualCameraMode ? (
              /* Dual Camera Layout */
              <div className="grid grid-cols-2 gap-4">
                {/* Left Camera */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 font-semibold text-sm">Left Camera (Primary)</span>
                    {detectionData && (
                      <span className="text-gray-400 text-xs">
                        {detectionData.count} objects • {detectionData.detection_time?.toFixed(3)}s
                      </span>
                    )}
                  </div>
                  {detectionData ? (
                    <div className="relative inline-block w-full">
                      <img
                        ref={imageRef}
                        src={detectionData.frame_base64}
                        alt="Left camera"
                        className="w-full rounded-lg"
                      />
                      <canvas
                        ref={canvasRef}
                        className="absolute top-0 left-0 w-full h-full pointer-events-none"
                      />
                      {autoRefresh && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded-full">
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          <span className="text-white text-xs font-semibold">LIVE</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-700 rounded-lg h-[240px] flex items-center justify-center">
                      <p className="text-gray-500 text-sm">No data</p>
                    </div>
                  )}
                </div>

                {/* Right Camera */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-400 font-semibold text-sm">Right Camera</span>
                    {rightCameraData && (
                      <span className="text-gray-400 text-xs">
                        {rightCameraData.count} objects • {rightCameraData.detection_time?.toFixed(3)}s
                      </span>
                    )}
                  </div>
                  {rightCameraData ? (
                    <div className="relative inline-block w-full">
                      <img
                        ref={rightImageRef}
                        src={rightCameraData.frame_base64}
                        alt="Right camera"
                        className="w-full rounded-lg"
                      />
                      <canvas
                        ref={rightCanvasRef}
                        className="absolute top-0 left-0 w-full h-full pointer-events-none"
                      />
                      {autoRefresh && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded-full">
                          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          <span className="text-white text-xs font-semibold">LIVE</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-700 rounded-lg h-[240px] flex items-center justify-center">
                      <p className="text-gray-500 text-sm">No data</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Single Camera Layout */
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                {detectionData ? (
                  <div className="relative inline-block w-full">
                    <img
                      ref={imageRef}
                      src={detectionData.frame_base64}
                      alt="Detection result"
                      className="w-full rounded-lg"
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                    />
                    {/* Live indicator */}
                    {autoRefresh && (
                      <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 px-3 py-1 rounded-full">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        <span className="text-white text-sm font-semibold">LIVE</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-700 rounded-lg h-[480px] flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-gray-400 mb-2">No detection data yet</p>
                      <p className="text-gray-500 text-sm">Click "Run Once" or "Start Live" to begin</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Class Counts */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <h2 className="text-lg font-bold text-white mb-3">Detected Classes</h2>
              {detectionData && Object.keys(detectionData.class_counts).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(detectionData.class_counts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([className, count]) => (
                      <div
                        key={className}
                        className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: classColors[className] || getClassColor(className) }}
                          />
                          <span className="text-white text-sm truncate max-w-[120px]">
                            {className}
                          </span>
                        </div>
                        <span className="text-blue-400 font-bold">{count}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No detections</p>
              )}
            </div>

            {/* Detection List */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-[400px] overflow-y-auto">
              <h2 className="text-lg font-bold text-white mb-3">All Detections</h2>
              {detectionData && detectionData.detections.length > 0 ? (
                <div className="space-y-2">
                  {detectionData.detections
                    .sort((a, b) => b.confidence - a.confidence)
                    .map((det, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-700/50 rounded p-2 text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className="font-semibold truncate"
                            style={{ color: classColors[det.class_name] || getClassColor(det.class_name) }}
                          >
                            {det.class_name}
                          </span>
                          <span className="text-gray-300 font-mono text-xs">
                            {(det.confidence * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No detections</p>
              )}
            </div>

            {/* Barcode Results */}
            {enableBarcode && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Barcode size={20} className="text-purple-400" />
                  Barcodes
                </h2>
                {barcodeResults.length > 0 ? (
                  <div className="space-y-2">
                    {barcodeResults.map((barcode, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-700/50 rounded p-2 text-sm"
                      >
                        <div className="font-mono text-purple-300 text-xs mb-1">
                          {barcode.value}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {barcode.format}
                        </div>
                        {barcode.productName && (
                          <div className="text-green-400 text-xs mt-1 truncate">
                            {barcode.productName}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No barcodes detected</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
